import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button, Group, Select, TextInput, Checkbox, Stack, Text, Alert, Loader, Center, MultiSelect, Menu, ActionIcon, Tooltip } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconSearch, IconCopy, IconDots } from '@tabler/icons-react';
import { getAllAddressSystems } from '../../services/UserService';
import { getContractUsers, saveContractUsers, getContractStatuses } from '../../services/ContractUserService';
import { ContractUserRow, ContractUserSaveRequest, ContractAccessLevel, ContractStatus, ContractListItem } from '../../types/ContractUser';
import { AddressSystem } from '../../types/UserRole';
import { SortableTable, ColumnDef } from '../SortableTable';
import { useTranslation } from "../../hooks/useTranslation";
import useTransientStore from "../../data/transcientStore";

export default function ContractUsersManagement() {
  const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({ pageId: 337, addressSystemId: addrIdFromStore || 0, autoLoad: true });

  const accessLevelLabels = useMemo(() => ({
    [ContractAccessLevel.NoAccess]: {
      label: '-',
      title: getTranslation("LABEL_CONTRACT_USERS_NO_ACCESS") || "No access to this contract",
      color: '#ea2d07ff'
    },
    [ContractAccessLevel.RegularUser]: {
      label: 'K',
      title: getTranslation("LABEL_CONTRACT_USERS_REGULAR_USER") || "Regular user",
      color: ''
    },
    [ContractAccessLevel.BillingUser]: {
      label: 'L',
      title: getTranslation("LABEL_CONTRACT_USERS_BILLING_USER") || "Billing user",
      color: ''
    },
    [ContractAccessLevel.ContractorForeman]: {
      label: 'U',
      title: getTranslation("LABEL_CONTRACT_USERS_CONTRACTOR_FOREMAN") || "Contractor foreman",
      color: ''
    },
    [ContractAccessLevel.ContractorBilling]: {
      label: 'UL',
      title: getTranslation("LABEL_CONTRACT_USERS_CONTRACTOR_BILLING") || "Contractor billing",
      color: ''
    },
    [ContractAccessLevel.ClientForeman]: {
      label: 'T',
      title: getTranslation("LABEL_CONTRACT_USERS_CLIENT_FOREMAN") || "Client foreman",
      color: ''
    },
  }), [getTranslation]);

  const accessLevelSelectData = useMemo(() => Object.entries(accessLevelLabels).map(([level, config]) => ({
    value: level,
    label: `${config.label} - ${config.title}`
  })), [accessLevelLabels]);

  const [addressSystems, setAddressSystems] = useState<AddressSystem[]>([]);
  const [contractStatuses, setContractStatuses] = useState<ContractStatus[]>([]);
  const [selectedAddressSystemId, setSelectedAddressSystemId] = useState<string | null>(null);
  const [selectedContractStatusId, setSelectedContractStatusId] = useState<string | null>(null);
  const [selectedContractIds, setSelectedContractIds] = useState<string[]>(['allContracts']);
  const [searchTerm, setSearchTerm] = useState<string>('');
  // Debounced search term to avoid filtering on every keystroke
  const [debouncedSearch] = useDebouncedValue(searchTerm, 300);
  const [companyFilter, setCompanyFilter] = useState<string[]>([]);
  const [users, setUsers] = useState<ContractUserRow[]>([]);
  const [contracts, setContracts] = useState<ContractListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Fixed table page size (adjust if needed or add responsive calc)
  const pageSize = 100;
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [nameFilter, setNameFilter] = useState<string[]>([]);

  // Multi-select users functionality
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const selectedUsersRef = useRef(selectedUsers);
  selectedUsersRef.current = selectedUsers;

  // Store original user data for reset functionality
  const [originalUsers, setOriginalUsers] = useState<ContractUserRow[]>([]);

  // Track which row has an open action menu
  const [activeMenuUserId, setActiveMenuUserId] = useState<number | null>(null);

  // Load address systems
  useEffect(() => {
    loadAddressSystems();
  }, []);

  // (Dynamic pageSize effect removed for performance and determinism)

  // Load contract statuses when address system changes
  useEffect(() => {
    if (selectedAddressSystemId) {
      loadContractStatuses();
    } else {
      setContractStatuses([]);
      setSelectedContractStatusId(null);
    }
    // Clear selected users when address system changes
    setSelectedUsers(new Set());
  }, [selectedAddressSystemId]);

  // Load contract users when both address system and status are selected
  useEffect(() => {
    if (selectedAddressSystemId && selectedContractStatusId) {
      loadContractUsers();
    } else {
      setUsers([]);
      setContracts([]);
      setSelectedContractIds([]);
    }
    // Clear selected users when address system or status changes
    setSelectedUsers(new Set());
  }, [selectedAddressSystemId, selectedContractStatusId]);

  // Reset contract selection when contracts change
  useEffect(() => {
    if (selectedContractIds.includes('allContracts')) {
      setSelectedContractIds(['allContracts', ...contracts.map(c => c.id.toString())]);
    }
  }, [contracts]);

  const loadAddressSystems = async () => {
    try {
      const systems = await getAllAddressSystems();
      setAddressSystems(systems);
    } catch (error) {
      notifications.show({
        title: getTranslation("LABEL_CONTRACT_USERS_ERROR_TITLE") || "Error",
        message: getTranslation("LABEL_CONTRACT_USERS_LOAD_ADDRESS_SYSTEMS_FAILED") || "Failed to load address systems",
        color: 'red',
        withBorder: true,
        withCloseButton: true,
        autoClose: 5000,
        className: 'notification-error'
      });
    }
  };

  const loadContractStatuses = async () => {
    if (!selectedAddressSystemId) return;

    try {
      const statuses = await getContractStatuses(parseInt(selectedAddressSystemId));
      setContractStatuses(statuses);
    } catch (error) {
      notifications.show({
        title: getTranslation("LABEL_CONTRACT_USERS_ERROR_TITLE") || "Error",
        message: getTranslation("LABEL_CONTRACT_USERS_LOAD_STATUSES_FAILED") || "Failed to load contract statuses",
        color: 'red',
        withBorder: true,
        withCloseButton: true,
        autoClose: 5000,
        className: 'notification-error'
      });
    }
  };

  const loadContractUsers = async () => {
    if (!selectedAddressSystemId || !selectedContractStatusId) return;

    setLoading(true);
    try {
      // Load the contract users
      const response = await getContractUsers(
        parseInt(selectedAddressSystemId),
        parseInt(selectedContractStatusId)
      );

      setUsers(response.users);
      setContracts(response.contracts);
      setOriginalUsers(response.users); // Store original data for reset
      setSelectedContractIds(['allContracts', ...response.contracts.map(c => c.id.toString())]); // Select all contracts by default
    } catch (error) {
      notifications.show({
        title: getTranslation("LABEL_CONTRACT_USERS_ERROR_TITLE") || "Error",
        message: getTranslation("LABEL_CONTRACT_USERS_LOAD_USERS_FAILED") || "Failed to load contract users",
        color: 'red',
        withBorder: true,
        withCloseButton: true,
        autoClose: 5000,
        className: 'notification-error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter contracts based on selection
  const displayedContracts = useMemo(() => {
    if (selectedContractIds.includes('allContracts')) {
      return contracts;
    }
    return contracts.filter(contract => selectedContractIds.includes(contract.id.toString()));
  }, [contracts, selectedContractIds]);

  // Filter users based on search term, company filter and role filter 
  const filteredUsers = useMemo(() => {
    let filtered = users;
    if (debouncedSearch.trim()) {
      const searchLower = debouncedSearch.toLowerCase();
      filtered = filtered.filter(user =>
        user.username.toLowerCase().includes(searchLower) ||
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        (user.companyName || '').toLowerCase().includes(searchLower) ||
        (user.roleName || '').toLowerCase().includes(searchLower)
      );
    }
    if (nameFilter.length > 0) {
      filtered = filtered.filter(user => nameFilter.includes(user.name));
    }
    if (companyFilter.length > 0) {
      filtered = filtered.filter(user => companyFilter.includes(user.companyName));
    }
    if (roleFilter.length > 0) {
      filtered = filtered.filter(user => roleFilter.includes(user.roleName));
    }
    return filtered;
  }, [users, debouncedSearch, nameFilter, companyFilter, roleFilter]);

  // Pre-compute filter option lists (stable references for columns memo)
  const nameOptions = useMemo(() => Array.from(new Set(users.map(u => u.name)).values())
    .filter(Boolean)
    .map(name => ({ value: name as string, label: name as string })), [users]);
  const companyOptions = useMemo(() => Array.from(new Set(users.map(u => u.companyName)).values())
    .filter(Boolean)
    .map(name => ({ value: name as string, label: name as string })), [users]);
  const roleOptions = useMemo(() => Array.from(new Set(users.map(u => u.roleName)).values())
    .filter(Boolean)
    .map(name => ({ value: name as string, label: name as string })), [users]);

  const handleUserFieldChange = useCallback((userId: number, field: string, value: any) => {
    setUsers(prevUsers =>
      prevUsers.map(user =>
        user.id === userId
          ? { ...user, [field]: value }
          : user
      )
    );
  }, []);

  const handleAccessLevelChange = useCallback((userId: number, contractId: number, accessLevel: ContractAccessLevel) => {
    setUsers(prevUsers =>
      prevUsers.map(user =>
        user.id === userId
          ? {
            ...user,
            contractAccess: {
              ...user.contractAccess,
              [contractId]: accessLevel
            }
          }
          : user
      )
    );
  }, []);

  const handleUserSelect = useCallback((userId: number, checked: boolean) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
  }, [selectedUsers]);

  const handleSelectAllUsers = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
    } else {
      setSelectedUsers(new Set());
    }
  }, [filteredUsers]);

  const handleSave = async () => {
    if (!selectedAddressSystemId || !selectedContractStatusId) return;

    setSaving(true);
    try {
      const saveRequest: ContractUserSaveRequest = {
        addressSystemId: parseInt(selectedAddressSystemId),
        contractStatusId: parseInt(selectedContractStatusId),
        users: users.map(user => ({
          id: user.id,
          name: user.name,
          email: user.email,
          phoneNumber: user.phoneNumber,
          copyToRamapp: user.copyToRamapp,
          contractAccess: user.contractAccess
        }))
      };

      await saveContractUsers(saveRequest);

      notifications.show({
        title: getTranslation("LABEL_CONTRACT_USERS_SUCCESS_TITLE") || "Success",
        message: getTranslation("LABEL_CONTRACT_USERS_SAVED") || "Contract users saved successfully",
        color: 'green',
        withBorder: true,
        withCloseButton: true,
        autoClose: 5000,
        className: 'notification-success'
      });
    } catch (error) {
      notifications.show({
        title: getTranslation("LABEL_CONTRACT_USERS_ERROR_TITLE") || "Error",
        message: getTranslation("LABEL_CONTRACT_USERS_SAVE_FAILED") || "Failed to save contract users",
        color: 'red',
        withBorder: true,
        withCloseButton: true,
        autoClose: 5000,
        className: 'notification-error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResetAllChanges = () => {
    // Reset users to original state
    setUsers([...originalUsers]);
    // Clear user selections
    setSelectedUsers(new Set());

    // Show notification
    notifications.show({
      title: getTranslation("LABEL_CONTRACT_USERS_CHANGES_RESET_TITLE") || "All changes reset",
      message: getTranslation("LABEL_CONTRACT_USERS_CHANGES_RESET_MESSAGE") || "All changes have been reset to original values. User selections cleared.",
      color: 'blue',
      withBorder: true,
      withCloseButton: true,
      autoClose: 5000,
      className: 'notification-info'
    });
  };

  // const handleResetSelections = () => {
  //   // Reset users to original state 
  //   setUsers([...originalUsers]);

  //   // Show notification
  //   notifications.show({
  //     title: 'All changes reset',
  //     message: 'All changes have been reset to original values. User selections remain.',
  //     color: 'blue',
  //   });
  // };

  const handleCopyFromUser = useCallback((sourceUserId: number) => {
    const sourceUser = users.find(u => u.id === sourceUserId);
    if (!sourceUser) return;

    // Copy access levels from source user to all selected users
    setUsers(prevUsers =>
      prevUsers.map(user => {
        if (selectedUsers.has(user.id)) {
          return {
            ...user,
            contractAccess: { ...sourceUser.contractAccess }
          };
        }
        return user;
      })
    );

    // Show notification
    const userCount = selectedUsers.size;
    notifications.show({
      title: getTranslation("LABEL_CONTRACT_USERS_SUBROLES_COPIED_TITLE") || "Subroles copied",
      message: getTranslation("LABEL_CONTRACT_USERS_SUBROLES_COPIED_MESSAGE") || `Subroles from ${sourceUser.username} copied to ${userCount} selected ${userCount === 1 ? 'user' : 'users'}.`,
      color: 'green',
      withBorder: true,
      withCloseButton: true,
      autoClose: 5000,
      className: 'notification-success'
    });
  }, [users, selectedUsers]);

  const handleBulkAccessLevelChange = useCallback((contractId: number, accessLevel: ContractAccessLevel) => {
    // Apply the access level to all selected users for this contract
    setUsers(prevUsers =>
      prevUsers.map(user => {
        if (selectedUsers.has(user.id)) {
          return {
            ...user,
            contractAccess: {
              ...user.contractAccess,
              [contractId]: accessLevel
            }
          };
        }
        return user;
      })
    );
  }, [selectedUsers]);

  const renderAccessSelect = useCallback((user: ContractUserRow, contract: ContractListItem) => {
    const currentAccess = user.contractAccess[contract.id] || ContractAccessLevel.NoAccess;
    const isUserSelected = selectedUsersRef.current.has(user.id);
    const isBulkEdit = isUserSelected && selectedUsersRef.current.size > 1;

    return (
      <select
        value={currentAccess.toString()}
        onChange={(e) => {
          const accessLevel = parseInt(e.target.value) as ContractAccessLevel;
          if (isBulkEdit) {
            handleBulkAccessLevelChange(contract.id, accessLevel);
          } else {
            handleAccessLevelChange(user.id, contract.id, accessLevel);
          }
        }}
        title={`${getTranslation("LABEL_CONTRACT_USERS_ACCESS_LEVEL_FOR") || "Access level for"} ${contract.name}`}
        aria-label={`${getTranslation("LABEL_CONTRACT_USERS_ACCESS_LEVEL_FOR") || "Access level for"} ${contract.name}`}
        style={{
          width: '100%',
          padding: '6px 8px',
          fontSize: '13px',
          borderRadius: '4px',
          border: '1px solid #ced4da',
          backgroundColor:
            currentAccess === ContractAccessLevel.NoAccess
              ? '#ffeaea'
              : isBulkEdit
                ? '#e3f2fd'
                : '#fff',
          color: currentAccess === ContractAccessLevel.NoAccess ? '#c92a2a' : '#212529',
          cursor: 'pointer',
          outline: 'none',
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#228be6';
          e.target.style.boxShadow = '0 0 0 2px rgba(34, 139, 230, 0.2)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = '#ced4da';
          e.target.style.boxShadow = 'none';
        }}
      >
        {accessLevelSelectData.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }, [handleBulkAccessLevelChange, handleAccessLevelChange]);

  // Define table columns (memoized to avoid re-creating on each render)
  const columns: ColumnDef<ContractUserRow>[] = useMemo(() => [
    {
      header: (
        <Checkbox
          checked={selectedUsersRef.current.size === filteredUsers.length && filteredUsers.length > 0}
          indeterminate={selectedUsersRef.current.size > 0 && selectedUsersRef.current.size < filteredUsers.length}
          onChange={(event) => handleSelectAllUsers(event.currentTarget.checked)}
        />
      ),
      accessor: 'select',
      width: 60,
      render: (user) => (
        <Checkbox
          checked={selectedUsersRef.current.has(user.id)}
          onChange={(event) => handleUserSelect(user.id, event.currentTarget.checked)}
        />
      )
    },
    // {
    //   header: 'Username',
    //   accessor: 'username',
    //   sortable: true,
    //   width: 120,
    //   filter: {
    //     type: 'multiselect',
    //     value: usernameFilter,
    //     onChange: setUsernameFilter,
    //     options: () => Array.from(new Set(users.map(u => u.username)).values())
    //       .filter(Boolean)
    //       .map(name => ({ value: name, label: name })),
    //     placeholder: 'Filter usernames...'
    //   }
    // },
    {
      header: getTranslation("LABEL_CONTRACT_USERS_NAME") || "Name",
      accessor: 'name',
      sortable: true,
      width: 170,
      render: (user) => (
        <span title={`Username: ${user.username}`}>
          {user.name || '-'}
        </span>
      ),
      filter: {
        type: 'multiselect',
        value: nameFilter,
        onChange: setNameFilter,
        options: () => nameOptions,
        placeholder: getTranslation("PLACEHOLDER_CONTRACT_USERS_FILTER_NAMES") || "Filter names..."
      }
    },
    {
      // header: 'Company',
      header: getTranslation("LABEL_CONTRACT_USERS_ORGANISATION") || "Organisation",
      accessor: 'companyName',
      sortable: true,
      width: 160,
      render: (user) => user.companyName || '-',
      filter: {
        type: 'multiselect',
        value: companyFilter,
        onChange: setCompanyFilter,
        options: () => companyOptions,
        // placeholder: 'Filter companies...'
        placeholder: getTranslation("PLACEHOLDER_CONTRACT_USERS_FILTER_ORGS") || "Filter organisations..."
      }
    },
    {
      header: getTranslation("LABEL_CONTRACT_USERS_ROLE") || "Role",
      accessor: 'roleName',
      sortable: true,
      width: 170,
      render: (user) => user.roleName || '-',
      filter: {
        type: 'multiselect',
        value: roleFilter,
        onChange: setRoleFilter,
        options: () => roleOptions,
        placeholder: getTranslation("PLACEHOLDER_CONTRACT_USERS_FILTER_ROLES") || "Filter roles..."
      }
    },
    {
      header: getTranslation("LABEL_CONTRACT_USERS_EMAIL") || "Email",
      accessor: 'email',
      width: 170,
      render: (user) => (
        <TextInput
          value={user.email}
          onChange={(e) => handleUserFieldChange(user.id, 'email', e.target.value)}
          size="xs"
          radius="sm"
          style={{ width: '100%' }}
          spellCheck={false}
        />
      ),
    },
    {
      header: getTranslation("LABEL_CONTRACT_USERS_PHONE") || "Phone",
      accessor: 'phoneNumber',
      width: 120,
      render: (user) => (
        <TextInput
          value={user.phoneNumber}
          onChange={(e) => handleUserFieldChange(user.id, 'phoneNumber', e.target.value)}
          size="xs"
          radius="sm"
          style={{ width: '100%' }}
          spellCheck={false}
        />
      ),
    },
    {
      header: (
        <Tooltip label={getTranslation("LABEL_RAMAPP") || "RamApp"} withArrow>
          <Text component="span" fw={500}>RA</Text>
        </Tooltip>
      ),
      accessor: 'copyToRamapp',
      width: 60,
      render: (user) => (
        <Checkbox
          checked={user.copyToRamapp}
          onChange={(e) => handleUserFieldChange(user.id, 'copyToRamapp', e.target.checked)}
        />
      ),
    },
    {
      header: getTranslation("LABEL_CONTRACT_USERS_ACTIONS") || "Actions",
      accessor: 'actions',
      width: 60,
      render: (user) => {
        const noContractsSelected =
          selectedContractIds.length === 0 ||
          (selectedContractIds.length === 1 && selectedContractIds[0] === 'allContracts');
        return (
          <Menu shadow="md" width={200} onOpen={() => setActiveMenuUserId(user.id)} onClose={() => setActiveMenuUserId(null)}>
            <Menu.Target>
              <ActionIcon variant="subtle" size="sm" disabled={noContractsSelected}>
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconCopy size={14} />}
                onClick={() => handleCopyFromUser(user.id)}
                disabled={selectedUsersRef.current.size === 0 || noContractsSelected}
                style={{ whiteSpace: 'nowrap' }}
              >
                {getTranslation("LABEL_CONTRACT_USERS_COPY_SUBROLES") || "Copy contract subroles"}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        );
      },
    },
    // Dynamic columns for each contract
    ...displayedContracts.map(contract => ({
      header: (
        <Stack gap={4}>
          <Text size="sm" fw={500}>{contract.name}</Text>
          {/* {selectedUsers.size > 1 && (
            <Text size="xs" c="blue" style={{ fontStyle: 'italic' }}>
              {selectedUsers.size} users selected - changes apply to all
            </Text>
          )} */}
        </Stack>
      ),
      accessor: `contract_${contract.id}` as keyof ContractUserRow,
      width: 250,
      render: (user: ContractUserRow) => renderAccessSelect(user, contract),
    })),
  ], [
    filteredUsers.length,
    handleSelectAllUsers,
    handleUserSelect,
    nameFilter,
    companyFilter,
    roleFilter,
    nameOptions,
    companyOptions,
    roleOptions,
    displayedContracts,
    selectedContractIds,
    handleCopyFromUser,
    handleUserFieldChange,
    renderAccessSelect
  ]);

  return (
    <>
      <Group justify="space-between" align="end" mb="xs">
        <Select
          label={getTranslation("LABEL_ADDRESS_SYSTEM") || "Address System"}
          placeholder={getTranslation("PLACEHOLDER_SELECT_ADDRESS_SYSTEM") || "Select address system"}
          value={selectedAddressSystemId}
          onChange={(value) => {
            setSelectedAddressSystemId(value);
            setSelectedContractStatusId(null); // Reset contract status
            setUsers([]);                     // Clear users
            setContracts([]);                 // Clear contracts
            setSelectedContractIds([]);       // Clear contract selection
          }}
          data={addressSystems.map(system => ({
            value: system.id.toString().trim(),
            label: system.name.trim(),
            // label: `${system.name} (address_system: ${system.id})`
          }))}
          style={{ flex: 1, minWidth: '200px' }}
          searchable
          spellCheck={false}
        />
        <Select
          label={getTranslation("LABEL_STATUS") || "Status"}
          placeholder={getTranslation("PLACEHOLDER_SELECT_CONTRACT_STATUS") || "Select contract status"}
          value={selectedContractStatusId}
          onChange={setSelectedContractStatusId}
          data={contractStatuses.map(status => ({
            value: status.id.toString(),
            label: status.name
          }))}
          style={{ flex: 1, minWidth: '200px', marginLeft: '16px' }}
          disabled={!selectedAddressSystemId}
          searchable
          spellCheck={false}
        />
        <TextInput
          label={getTranslation("LABEL_SEARCH") || "Search"}
          placeholder={getTranslation("PLACEHOLDER_SEARCH_USERS") || "Search users..."}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.currentTarget.value)}
          leftSection={<IconSearch size={18} color="#009DF0" />}
          style={{ flex: 1, minWidth: '200px', marginLeft: '16px' }}
          disabled={!selectedAddressSystemId || !selectedContractStatusId}
          spellCheck={false}
        />
      </Group>

      <Group justify="flex-start" align="end" mb="md">
        <MultiSelect
          label={getTranslation("LABEL_CONTRACTS") || "Contracts"}
          placeholder={selectedContractIds.length === 0 ? (getTranslation("PLACEHOLDER_CONTRACT_USERS_SELECT_CONTRACTS") || "Select contracts") : ""}
          value={selectedContractIds.includes('allContracts')
            ? contracts.map(c => c.id.toString())
            : selectedContractIds
          }
          onChange={(value) => {
            if (value.includes('allContracts')) {
              setSelectedContractIds(['allContracts', ...contracts.map(c => c.id.toString())]);
            } else {
              if (value.length === contracts.length) {
                setSelectedContractIds(['allContracts', ...value]);
              } else {
                setSelectedContractIds(value.filter(id => id !== 'allContracts'));
              }
            }
          }}
          data={[
            ...(selectedContractIds.includes('allContracts') ? [] : [{ value: 'allContracts', label: getTranslation("LABEL_CONTRACT_USERS_ALL_CONTRACTS") || "All Contracts" }]),
            ...contracts.map(contract => ({
              value: contract.id.toString(),
              label: contract.name
            }))
          ]}
          style={{ minWidth: '600px', flex: 1 }}
          styles={{
            input: {
              minHeight: '72px',
              maxHeight: '72px',
              overflow: 'auto'
            },
            pillsList: {
              flexWrap: 'wrap',
              overflow: 'auto',
              maxHeight: '48px',
              position: 'relative',
              left: '0px',
              paddingLeft: '8px',
              paddingRight: '8px'
            }
          }}
          searchable
          hidePickedOptions={false}
          clearable
          withCheckIcon
          disabled={!selectedAddressSystemId || !selectedContractStatusId}
        />
      </Group>

      {!selectedAddressSystemId && (
        <Alert icon={<IconAlertCircle size={18} />} color="blue">
          {getTranslation("LABEL_CONTRACT_USERS_SELECT_ADDRESS_SYSTEM") || "Please select an address system to continue."}
        </Alert>
      )}

      {selectedAddressSystemId && !selectedContractStatusId && (
        <Alert icon={<IconAlertCircle size={18} />} color="blue">
          {getTranslation("LABEL_CONTRACT_USERS_SELECT_STATUS") || "Please select a contract status to continue."}
        </Alert>
      )}

      {selectedAddressSystemId && selectedContractStatusId && (
        <>
          {selectedUsers.size > 1 && (
            <Alert color="blue" mb="md">
              <Group justify="space-between" align="center">
                <Text size="sm">
                  <strong>
                    {(getTranslation("LABEL_CONTRACT_USERS_SELECTED_COUNT") || "Users selected:")} {selectedUsers.size}
                  </strong> {getTranslation("LABEL_CONTRACT_USERS_BULK_HELP") || "Changes to contract subroles apply to all selected users. You can also use the actions menu (⋯) to copy subroles from another user."}
                </Text>
              </Group>
            </Alert>
          )}
          {selectedUsers.size === 1 && (
            <Alert color="green" mb="md">
              <Group justify="space-between" align="center">
                <Text size="sm">
                  <strong>
                    {(getTranslation("LABEL_CONTRACT_USERS_SELECTED_ONE") || "User selected:")} 1
                  </strong> {getTranslation("LABEL_CONTRACT_USERS_SINGLE_HELP") || "You can use the actions menu (⋯) to copy subroles from another user."}
                </Text>
              </Group>
            </Alert>
          )}
          {loading && (
            <Center py="xl">
              <Loader size="lg" />
            </Center>
          )}
          {!loading && users.length > 0 && (
            <>
              <SortableTable
                data={filteredUsers}
                columns={columns}
                rowKey={(row) => row.id.toString()}
                pageSize={pageSize}
                showPagination={true}
                rowStyle={(row) => {
                  const isSelected = selectedUsers.has(row.id);
                  const hasActiveMenu = activeMenuUserId === row.id;
                  if (hasActiveMenu) {
                    return {
                      backgroundColor: '#fff3cd',
                      border: '2px solid #ffc107',
                      boxShadow: '0 0 0 1px #ffc107'
                    };
                  }
                  return isSelected ? { backgroundColor: '#e3f2fd' } : {};
                }}
              />
              <Group justify="start" mt="md" gap="md">
                <Button
                  variant="outline"
                  onClick={handleResetAllChanges}
                  disabled={saving}
                >
                  {getTranslation("LABEL_CONTRACT_USERS_RESET_ALL") || "Reset All Changes"}
                </Button>
                <Button
                  onClick={handleSave}
                  loading={saving}
                  disabled={saving}
                >
                  {getTranslation("LABEL_CONTRACT_USERS_SAVE_CHANGES") || "Save Changes"}
                </Button>
              </Group>
            </>
          )}
          {!loading && users.length === 0 && (
            <Alert icon={<IconAlertCircle size="1rem" />} color="blue">
              {getTranslation("LABEL_CONTRACT_USERS_NO_USERS") || "No users found for the selected criteria."}
            </Alert>
          )}
        </>
      )}
    </>
  );
}